import { useI18n } from 'app/components/localization';
import ErrorBoundaryState from 'app/components/mixins/ErrorBoundaryState';
import { ButtonA } from 'app/components/presentation/buttons/ButtonClassNameA';
import { contentsStyles } from 'app/components/presentation/text/styles';
import { useClasses, IStyles } from 'app/utils/css';
import { notify } from 'app/services/errorReporter';
import React, { useEffect } from 'react';
import { getWindowOrThrow } from 'app/utils/env';

interface IProps {
  error?: Error;
  resetError: () => void;
  overrideStyles?: IStyles;
}

function ErrorHandler({
  error,
  resetError,
  overrideStyles,
}: IProps) {
  const { t } = useI18n();
  const classes = useClasses({
    container: {
      textAlign: 'center',
      margin: 'auto',
      padding: '1em',
      backgroundColor: 'white',
      ...overrideStyles,
    },
    contents: contentsStyles,
  });

  // Notify on mount (or if error changes somehow)
  useEffect(() => {
    notify(error, { name: 'ContentErrorBoundary' });
  }, [error]);

  const onClick = () => {
    resetError();
    try {
      getWindowOrThrow().location.reload();
    } catch (e) {
      // In the error boundary so notify but there's not much else to do.
      notify(e, { name: 'ContentErrorBoundary' });
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.contents}>
        <p>{t('content-error-boundary.message')}</p>

        <ButtonA onClick={onClick}>
          {t('content-error-boundary.refresh-button')}
        </ButtonA>
      </div>
    </div>
  );
}

const ContentErrorBoundary: React.FunctionComponent<{
  overrideStyles?: IStyles,
}> = ({ children, overrideStyles }) => {
  return (
    <ErrorBoundaryState>{(errorBoundaryProps) => errorBoundaryProps.error === undefined ?
      children : <ErrorHandler {...errorBoundaryProps} overrideStyles={overrideStyles} />
    }</ErrorBoundaryState>
  );
};

export default ContentErrorBoundary;
